import { useQuery } from "@tanstack/react-query";
import { userApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type { UserProfileResponse } from "@bw/shared/types/generated";

/**
 * The single profile-existence query.
 *
 * `useMyProfile` is the ONE place the app reads GET /users/profile. Both the
 * app-level `RequireProfile` gate and the onboarding module consume it, so
 * "does this user have a profile?" lives in exactly one cache entry per
 * authenticated identity — the onboarding create mutation invalidates this
 * key prefix to force a fresh read.
 *
 * It lives in `common/` (not the onboarding module) so the `app/` gate can
 * import it without deep-reaching into a module's internals — both `app/` and
 * the onboarding module depend on this one common surface.
 *
 * The query is deliberately NOT 404-tolerant: a missing profile surfaces as a
 * thrown ApiError(status=404) so RequireProfile can branch on it. Retries are
 * off — a 404 is a definitive answer, not a transient failure to retry.
 *
 * The type comes from `@bw/shared/types/generated` (ADR-005): the generated
 * `UserProfileResponse` has `account_status` / `requires_parental_consent` as
 * REQUIRED — the real backend contract — not the legacy hand-written optionals.
 */
export const profileQueryKey = ["user", "profile", "me"] as const;

// Partition identity-scoped data before effects clear the previous session's
// cache, so a direct A-to-B auth transition cannot render A's profile for B.
export function profileQueryKeyForUser(userId: string | null) {
  return [...profileQueryKey, userId ?? "anonymous"] as const;
}

/**
 * @param enabled gate the fetch (default `true`). The app-level `RequireProfile`
 *   gate only mounts inside the authed subtree, so it leaves this `true`. The
 *   public-browse surfaces (AppBar, HomePage) pass `Boolean(user)` so an
 *   ANONYMOUS visitor never fires this authed `/users/profile` read (no 401).
 */
export function useMyProfile(enabled = true) {
  const { user } = useAuth();
  const userId = user?.uid ?? null;

  return useQuery<UserProfileResponse>({
    queryKey: profileQueryKeyForUser(userId),
    enabled: enabled && userId !== null,
    // `userApi.getMyProfile` is now typed against the GENERATED
    // UserProfileResponse (#1311), so account_status / requires_parental_consent
    // are REQUIRED at the boundary — no narrowing cast needed.
    queryFn: () => userApi.getMyProfile(),
    retry: false,
  });
}
